/**
 * Salinan bahasa Indonesia untuk lapisan "near me" — gelombang 2: seluruh
 * layanan di luar 9 klaster utama.
 *
 * Ditulis manual (tanpa AI credit). Fakta selaras dengan ./en-copy-2.ts.
 *  - Thailand bukan anggota Konvensi Apostille Den Haag; dokumen Thailand untuk
 *    luar negeri melewati Departemen Konsuler (Kemlu Thailand) dan, bila perlu,
 *    kedutaan negara tujuan di Thailand.
 *  - Tidak ada harga dipublikasikan, penawaran per kasus; tidak ada janji waktu
 *    atau hasil — penerimaan berkas adalah kewenangan instansi penerima.
 *  - Kami bukan sekadar pelaksana: tim dengan pengalaman lebih dari 15 tahun di
 *    dokumen lintas negara mendampingi Anda menentukan urutan dan bukti sebelum
 *    pekerjaan dimulai.
 */

import type { NearServiceKey } from "./registry";
import type { NearIdCopy } from "./id-copy";

export const NEAR_ID2_KEYS = [
  "marriage",
  "longstay",
  "corporate",
  "immigration",
  "estate",
  "civilreg",
  "family",
  "accounting",
  "interpreter",
  "embassy",
  "ip",
  "litigation",
  "pdpa",
  "misc-official",
  "council",
  "due-diligence",
  "pension",
  "work-permit",
  "expert",
] as const satisfies readonly NearServiceKey[];

export type NearId2Key = (typeof NEAR_ID2_KEYS)[number];

const MFA =
  "Bila instansi penerima memintanya, berkas dilanjutkan ke Departemen Konsuler (Kementerian Luar Negeri Thailand), lalu ke kedutaan negara tujuan di Thailand.";

export const NEAR_ID2_COPY: Record<NearId2Key, NearIdCopy> = {
  marriage: {
    label: "Dokumen pernikahan Thailand – warga negara asing",
    definition:
      "Pencatatan pernikahan antara warga Thailand dan warga asing adalah rangkaian dokumen, bukan satu kali janji temu: mengurus surat keterangan bebas halangan menikah (belum menikah) di kedutaan Anda di Thailand, menerjemahkannya ke bahasa Thai secara bersertifikat, melegalisasi terjemahan di Kemlu, lalu mencatatkan pernikahan di kantor distrik (amphoe).",
    chain: [
      "Pastikan prosedur dan cara membuat janji temu terbaru di kedutaan Anda untuk surat keterangan bebas halangan menikah.",
      "Terima surat kedutaan, lalu penerjemah bersertifikat menerjemahkannya ke bahasa Thai.",
      "Legalisasi terjemahan di Departemen Konsuler (Kementerian Luar Negeri).",
      "Kedua pihak hadir mencatatkan pernikahan di kantor distrik.",
      "Ambil kutipan akta nikah, lalu terjemahkan dan legalisasi untuk dipakai di luar negeri.",
    ],
    checklist: [
      "Paspor pihak asing beserta cap masuk imigrasi.",
      "KTP dan kartu keluarga (tabien baan) pihak Thailand.",
      "Surat keterangan bebas halangan menikah / belum menikah dari kedutaan.",
      "Akta cerai atau akta kematian bila pernah menikah.",
      "Dua orang saksi saat pencatatan di kantor distrik.",
    ],
    pitfalls: [
      "Membuat janji di kantor distrik sebelum legalisasi Kemlu selesai.",
      "Nama pada surat kedutaan berbeda dengan paspor.",
      "Bukti berakhirnya pernikahan sebelumnya tidak dilampirkan.",
      "Mengira akta nikah Thailand otomatis diakui di luar negeri tanpa terjemahan dan legalisasi.",
    ],
    remote:
      "Terjemahan, legalisasi, dan penyusunan urutan kami tangani; janji temu kedutaan dan pencatatan perlu kehadiran kedua pihak.",
  },
  longstay: {
    label: "Berkas perpanjangan tinggal jangka panjang dan pensiun",
    definition:
      "Perpanjangan izin tinggal jangka panjang diputuskan Kantor Imigrasi berdasarkan bukti yang dilampirkan. Peran kami adalah penyiapan berkas dan konsultasi: mencocokkan bukti dengan kategori yang tepat, dalam format yang diterima instansi.",
    chain: [
      "Menentukan kategori perpanjangan yang tepat dan daftar bukti yang berlaku.",
      "Membandingkan dengan dokumen yang Anda miliki untuk menemukan kekurangan.",
      "Menyiapkan bukti keuangan, tempat tinggal, dan asuransi dalam format yang diterima.",
      "Menyusun berkas sesuai urutan pengajuan beserta lembar cek.",
      "Pendampingan saat pengajuan dan penanganan permintaan tambahan.",
    ],
    checklist: [
      "Paspor dengan visa berjalan dan TM.6 / data kedatangan.",
      "Pelaporan alamat TM.30 serta kontrak sewa atau bukti kepemilikan.",
      "Dokumen bank sesuai format dan lama pengendapan saldo yang diminta.",
      "Dokumen asuransi kesehatan bila kategori tersebut mensyaratkannya.",
      "Akta nikah atau bukti tanggungan untuk kategori keluarga.",
    ],
    pitfalls: [
      "Bukti bank belum memenuhi lama pengendapan yang ditentukan.",
      "TM.30 tidak ada, padahal justru diperiksa sebelum perpanjangan dinilai.",
      "Mengajukan terlalu dekat dengan tanggal berakhirnya izin tinggal.",
      "Percaya perpanjangan bisa dijamin — keputusan ada pada petugas penilai.",
    ],
    remote:
      "Penyiapan dan peninjauan berkas jarak jauh; janji temu di Kantor Imigrasi perlu kehadiran.",
  },
  corporate: {
    label: "Dokumen korporasi dan perusahaan",
    definition:
      "Dokumen korporasi mencakup berkas perusahaan, affidavit, kutipan DBD, resolusi direksi, dan pengesahannya untuk dipakai di luar negeri — umumnya untuk pembukaan rekening, tender, kontrak distribusi, atau pendirian anak perusahaan.",
    chain: [
      "Konfirmasi daftar dokumen persis yang diminta pihak penerima dan seberapa baru terbitannya.",
      "Mengambil kutipan DBD dan affidavit terbaru.",
      "Menyusun resolusi atau surat kuasa dengan rumusan yang diterima mitra.",
      "Notarisasi tanda tangan dan salinan bila diperlukan.",
      MFA,
    ],
    checklist: [
      "Company affidavit dan sertifikat DBD terbitan terkini.",
      "Daftar pemegang saham dan kutipan bidang usaha.",
      "Paspor atau KTP direktur berwenang.",
      "Draf resolusi atau kontrak dalam versi final.",
      "Permintaan tertulis mitra mengenai set dokumen.",
    ],
    pitfalls: [
      "Kutipan lebih lama dari masa berlaku yang ditetapkan mitra.",
      "Menandatangani sebelum jadwal pengesahan.",
      "Resolusi ditandatangani direktur yang tidak berwenang menurut affidavit.",
      "Terjemahan melewatkan tulisan pada cap kutipan berbahasa Thai.",
    ],
    remote: "Semua tahap kecuali penyaksian tanda tangan bisa dikerjakan jarak jauh.",
  },
  immigration: {
    label: "Penyiapan berkas dan konsultasi keimigrasian",
    definition:
      "Prosedur keimigrasian — perpanjangan, re-entry permit, perubahan kategori tinggal, pelaporan — masing-masing punya daftar bukti sendiri. Kami menyiapkan dan mengurutkan berkas; Kantor Imigrasi yang memutuskan.",
    chain: [
      "Konfirmasi jenis prosedur, kantor yang berwenang, dan daftar buktinya.",
      "Meninjau izin tinggal berjalan dan pengajuan sebelumnya agar konsisten.",
      "Menyiapkan dan mengesahkan dokumen pendukung.",
      "Menyusun sesuai urutan pengajuan dengan lembar cek di loket.",
      "Pemantauan dan tanggapan atas permintaan tambahan.",
    ],
    checklist: [
      "Paspor beserta seluruh halaman visa dan cap.",
      "Pelaporan alamat TM.30 dan dokumen tempat tinggal.",
      "Dokumen kerja, studi, atau keluarga sesuai kategori yang diajukan.",
      "Foto sesuai spesifikasi yang berlaku.",
      "Surat kuasa bila pengajuan diwakilkan.",
    ],
    pitfalls: [
      "Mengajukan ke kantor yang tidak berwenang atas wilayah Anda.",
      "Alamat tidak sama antara TM.30, kontrak sewa, dan formulir permohonan.",
      "Melewati batas izin tinggal saat masih mengumpulkan bukti.",
      "Berpegang pada daftar cek lama di internet, bukan versi terbaru kantor terkait.",
    ],
    remote: "Penyiapan dan konsultasi jarak jauh; janji temu di loket perlu kehadiran.",
  },
  estate: {
    label: "Layanan waris, wasiat, dan harta peninggalan",
    definition:
      "Pewarisan di Thailand menggabungkan proses pengadilan dan pendaftaran: wasiat (atau pembagian menurut undang-undang), penetapan pengurus harta peninggalan oleh pengadilan, lalu balik nama tiap jenis aset di instansi pendaftaran terkait. Harta lintas negara menambah kebutuhan terjemahan dan legalisasi.",
    chain: [
      "Memetakan harta: aset, lokasi aset, urutan ahli waris, dan wasiat bila ada.",
      "Menentukan aset mana yang wajib melalui pengurus harta yang ditetapkan pengadilan.",
      "Menghimpun bukti catatan sipil dan hubungan waris, dengan terjemahan bersertifikat untuk dokumen asing.",
      "Mengajukan permohonan penetapan pengurus harta dan menghadiri sidang melalui pengacara.",
      "Balik nama tiap aset di instansi pendaftaran dengan penetapan pengadilan.",
    ],
    checklist: [
      "Akta kematian dan terjemahan bersertifikat bila diterbitkan di luar negeri.",
      "Wasiat (bila ada) dan lokasi penyimpanan dokumen aslinya.",
      "Bukti urutan ahli waris: kartu keluarga, akta kelahiran, akta nikah.",
      "Bukti aset: sertifikat tanah, rekening koran, buku pemegang saham.",
      "Paspor atau KTP seluruh ahli waris.",
    ],
    pitfalls: [
      "Mencoba balik nama rekening atau tanah sebelum ada penetapan pengurus harta.",
      "Dokumen asing belum memiliki terjemahan bahasa Thai yang dilegalisasi.",
      "Ada ahli waris yang terlewat sehingga pembagian bisa batal.",
      "Mengira wasiat luar negeri otomatis berlaku atas tanah di Thailand.",
    ],
    remote:
      "Pengumpulan dan penyusunan dokumen jarak jauh; pengacara menghadiri sidang, Anda hadir hanya bila diminta pengadilan.",
  },
  civilreg: {
    label: "Dokumen catatan sipil",
    definition:
      "Catatan sipil mencakup kelahiran, pernikahan, perceraian, kematian, perubahan nama, dan kartu keluarga yang dikelola kantor distrik. Tiap kutipan atau perbaikan punya daftar bukti sendiri, dan pemakaian di luar negeri menambah kebutuhan terjemahan serta legalisasi.",
    chain: [
      "Menentukan jenis dokumen dan kantor distrik yang menyimpan arsipnya.",
      "Mengajukan kutipan bersertifikat atau permohonan perbaikan data.",
      "Menghimpun bukti sesuai daftar kantor tersebut.",
      "Terjemahan bersertifikat untuk pemakaian di luar negeri.",
      MFA,
    ],
    checklist: [
      "KTP dan kartu keluarga Thailand, atau paspor untuk warga asing.",
      "Sertifikat atau kutipan yang sudah Anda pegang, bila ada.",
      "Bukti pendukung perbaikan data (dokumen sekolah, rumah sakit, atau pengadilan).",
      "Surat kuasa bila kami mengurus atas nama Anda.",
    ],
    pitfalls: [
      "Mengajukan di kantor yang tidak menyimpan arsip aslinya.",
      "Mengajukan perbaikan tanpa bukti mengenai data yang benar.",
      "Menerjemahkan kutipan yang kemudian diterbitkan ulang sehingga harus diulang.",
      "Transliterasi tidak konsisten antar dokumen anggota keluarga.",
    ],
    remote:
      "Pengajuan dan pemantauan kami tangani; sebagian perbaikan data memerlukan kehadiran pemilik data.",
  },
  family: {
    label: "Dokumen hukum keluarga",
    definition:
      "Urusan keluarga — perceraian, hak asuh, pengakuan anak, perwalian, perjanjian pranikah — diselesaikan di kantor distrik (bila sepakat) atau di pengadilan. Memilih jalur yang tepat sejak awal menghindarkan sebagian besar keterlambatan.",
    chain: [
      "Menilai apakah perkara bisa diselesaikan secara sepakat di kantor distrik atau harus ke pengadilan.",
      "Menyusun perjanjian atau gugatan dengan rumusan yang dapat dilaksanakan.",
      "Menghimpun bukti catatan sipil, dengan terjemahan bersertifikat untuk dokumen asing.",
      "Pencatatan di kantor distrik atau pengajuan ke pengadilan.",
      "Mengambil salinan resmi, menerjemahkan, dan melegalisasi untuk dipakai di luar negeri.",
    ],
    checklist: [
      "Akta nikah dan perjanjian pranikah bila ada.",
      "Akta kelahiran anak-anak.",
      "KTP, kartu keluarga, dan paspor kedua pihak.",
      "Bukti terkait hak asuh atau nafkah.",
      "Putusan pengadilan asing (bila ada) beserta terjemahan bersertifikat.",
    ],
    pitfalls: [
      "Bercerai secara sepakat tanpa perjanjian tertulis mengenai harta dan anak.",
      "Memakai putusan asing di Thailand tanpa proses pengakuan.",
      "Rumusan yang kemudian ditolak pelaksanaannya oleh kantor distrik atau bank.",
      "Mengira catatan Thailand dan luar negeri saling memperbarui otomatis — tidak demikian.",
    ],
    remote:
      "Penyusunan dokumen dan peninjauan bukti jarak jauh; pencatatan dan sidang perlu kehadiran.",
  },
  accounting: {
    label: "Dokumen akuntansi, pajak, dan audit",
    definition:
      "Pekerjaan ini mencakup pembukuan, laporan keuangan, berkas audit, dan surat pemberitahuan pajak, beserta pengesahan dan terjemahannya ketika bank, panitia tender, atau induk perusahaan di luar negeri memintanya.",
    chain: [
      "Konfirmasi laporan apa dan periode mana yang diminta pihak penerima.",
      "Rekonsiliasi pembukuan dan penyusunan set laporan.",
      "Penandatanganan auditor bila dibutuhkan laporan teraudit.",
      "Terjemahan bersertifikat atas laporan dan catatan audit.",
      "Notarisasi dan, untuk pemakaian di luar negeri, legalisasi di Kementerian Luar Negeri.",
    ],
    checklist: [
      "Company affidavit dan bukti pendaftaran pajak.",
      "Neraca saldo, buku besar, dan rekening koran periode laporan.",
      "Laporan dan SPT tahun sebelumnya yang telah dilaporkan.",
      "Ketentuan format dari pihak penerima.",
    ],
    pitfalls: [
      "Menyerahkan laporan manajemen padahal yang dibutuhkan set teraudit.",
      "Terjemahan menghilangkan rumusan pada opini auditor.",
      "Mengesahkan versi sementara yang kemudian disesuaikan lagi.",
      "Melewatkan tenggat pelaporan resmi saat masih mengumpulkan berkas.",
    ],
    remote: "Sepenuhnya jarak jauh; set cetak bersertifikat dikirim melalui kurir.",
  },
  interpreter: {
    label: "Jasa juru bahasa untuk sidang dan janji temu",
    definition:
      "Juru bahasa adalah bantuan bahasa lisan di lokasi — sidang pengadilan, kantor pertanahan, kantor distrik, penandatanganan notarial, loket bank atau imigrasi. Berbeda dari terjemahan dokumen bersertifikat, dan banyak janji temu membutuhkan keduanya.",
    chain: [
      "Konfirmasi lokasi, pasangan bahasa, tanggal, dan perkiraan durasi.",
      "Memberi pembekalan istilah teknis kepada juru bahasa sebelum hari-H.",
      "Mengirim set dokumen agar istilah lisan sesuai berkas tertulis.",
      "Juru bahasa mendampingi Anda di lokasi janji temu.",
      "Catatan pascapertemuan dan terjemahan tertulis bila diminta instansi.",
    ],
    checklist: [
      "Lokasi, ruang, dan jam janji temu.",
      "Pasangan bahasa serta dialek bila ada permintaan khusus.",
      "Dokumen yang akan dibahas, dikirim lebih awal.",
      "Nama dan peran pihak yang hadir agar sapaan tepat.",
    ],
    pitfalls: [
      "Memesan terlalu mendadak untuk pasangan bahasa yang langka.",
      "Tanpa dokumen awal sehingga istilah harus diimprovisasi di loket.",
      "Mengira juru bahasa dapat langsung mengesahkan terjemahan tertulis di tempat.",
      "Memperkirakan durasi terlalu singkat sehingga jadwal terganggu.",
    ],
    remote:
      "Juru bahasa jarak jauh memungkinkan bila instansi mengizinkan; pengadilan dan kantor pendaftaran umumnya mensyaratkan kehadiran.",
  },
  embassy: {
    label: "Dokumen kedutaan dan konsuler",
    definition:
      "Pengesahan kedutaan adalah mata rantai terakhir banyak berkas luar negeri: setelah legalisasi Kemlu, kedutaan negara tujuan di Thailand mengesahkan dokumen. Tiap kedutaan punya aturan janji temu, rumusan, dan daftar penerjemah sendiri.",
    chain: [
      "Konfirmasi persyaratan terbaru kedutaan, termasuk daftar penerjemah yang diterima.",
      "Menyiapkan dokumen asli berbahasa Thai dan terjemahan bersertifikatnya.",
      "Menyelesaikan legalisasi Kemlu lebih dulu bila kedutaan mensyaratkannya.",
      "Membuat janji, mengajukan di kedutaan, dan mengambil set yang telah disahkan.",
      "Penyerahan berkas lengkap dengan lembar cek untuk instansi penerima.",
    ],
    checklist: [
      "Dokumen asli berbahasa Thai bertanda tangan dan bercap.",
      "Terjemahan bersertifikat ke bahasa yang diminta.",
      "Paspor atau KTP pemilik nama pada dokumen.",
      "Formulir kedutaan dan surat kuasa bila ada.",
    ],
    pitfalls: [
      "Memakai penerjemah di luar daftar yang diterima kedutaan.",
      "Mengajukan ke kedutaan sebelum legalisasi Kemlu selesai.",
      "Formulir atau foto khusus kedutaan tidak dilampirkan.",
      "Mengira semua kedutaan memakai urutan yang sama — tidak demikian.",
    ],
    remote:
      "Penyiapan dan pengajuan kami tangani pada kedutaan yang mengizinkan perwakilan.",
  },
  ip: {
    label: "Dokumen merek, hak cipta, dan kekayaan intelektual",
    definition:
      "Pekerjaan KI di Thailand berpusat pada pendaftaran dan pemeliharaan merek di Department of Intellectual Property, pencatatan hak cipta, serta akta pengalihan atau lisensi — banyak di antaranya perlu notarisasi dan legalisasi bila ditandatangani di luar negeri.",
    chain: [
      "Penelusuran kemungkinan pendaftaran dan pemilihan kelas barang/jasa sebelum pengajuan.",
      "Menyiapkan permohonan, contoh merek, dan surat kuasa.",
      "Mengajukan dan memantau pemeriksaan; menanggapi surat kantor.",
      "Mengesahkan dan melegalisasi surat kuasa, akta pengalihan, atau lisensi yang ditandatangani di luar negeri.",
      "Mencatat pengalihan, lisensi, dan perpanjangan tepat waktu.",
    ],
    checklist: [
      "Contoh merek dalam bentuk yang benar-benar dipakai.",
      "Dokumen perusahaan atau KTP pemohon.",
      "Daftar barang dan jasa untuk pemilihan kelas.",
      "Surat kuasa yang telah ditandatangani (dinotarisasi dan dilegalisasi bila ditandatangani di luar negeri).",
      "Bukti penggunaan pertama, bila akan diajukan sebagai dalil.",
    ],
    pitfalls: [
      "Mengajukan tanpa penelusuran awal lalu menghadapi keberatan.",
      "Daftar kelas terlalu sempit dibanding kegiatan usaha sebenarnya.",
      "Surat kuasa dari luar negeri tanpa pengesahan yang diperlukan.",
      "Melewati tenggat perpanjangan atau tenggat jawaban ke kantor merek.",
    ],
    remote:
      "Pengajuan dan penanganan permohonan jarak jauh; hanya pengesahan tanda tangan yang dapat memerlukan kehadiran.",
  },
  litigation: {
    label: "Dukungan litigasi dan dokumen pengadilan",
    definition:
      "Dukungan litigasi mencakup pernyataan tertulis, berkas bukti, terjemahan bersertifikat untuk bukti berbahasa asing, affidavit yang dinotarisasi, dan surat kuasa — disiapkan agar diterima pengadilan tanpa keberatan formal.",
    chain: [
      "Menilai perkara: kewenangan pengadilan, daluwarsa, dan celah pembuktian.",
      "Menyusun pernyataan tertulis dan menghimpun bukti sesuai format pengadilan.",
      "Terjemahan bersertifikat untuk tiap dokumen berbahasa asing.",
      "Notarisasi dan legalisasi dokumen yang ditandatangani di luar negeri.",
      "Pengajuan berkas, kehadiran pengacara di sidang, dan langkah pascaputusan.",
    ],
    checklist: [
      "Kontrak, korespondensi, dan seluruh rangkaian dokumen.",
      "Paspor atau dokumen perusahaan para pihak.",
      "Surat kuasa untuk pengacara (disahkan bila ditandatangani di luar negeri).",
      "Putusan asing atau putusan arbitrase beserta terjemahan bersertifikat.",
    ],
    pitfalls: [
      "Mengajukan bukti berbahasa asing tanpa terjemahan bahasa Thai bersertifikat.",
      "Surat kuasa dari luar negeri belum dinotarisasi dan dilegalisasi.",
      "Membiarkan daluwarsa lewat saat masih mengumpulkan bukti.",
      "Mengharapkan jaminan hasil — tidak ada pengacara yang dapat menjaminnya.",
    ],
    remote: "Penyusunan dokumen dan pengolahan bukti jarak jauh; pengacara menghadiri sidang.",
  },
  pdpa: {
    label: "Dokumen PDPA dan pelindungan data pribadi",
    definition:
      "Undang-undang Pelindungan Data Pribadi (PDPA) Thailand mewajibkan pelaku usaha memiliki dasar yang terdokumentasi saat memproses data pribadi: pemberitahuan privasi, catatan persetujuan, perjanjian pemrosesan data, daftar kegiatan pemrosesan, dan prosedur penanganan insiden.",
    chain: [
      "Memetakan data: data pribadi apa yang Anda simpan, untuk apa, dan mengalir ke mana.",
      "Memilih dan mendokumentasikan dasar hukum tiap tujuan pemrosesan.",
      "Menyusun pemberitahuan privasi, formulir persetujuan, dan perjanjian pemrosesan data.",
      "Membangun daftar kegiatan pemrosesan dan prosedur penanganan kebocoran.",
      "Melatih staf dan menetapkan siklus peninjauan saat kegiatan pemrosesan berubah.",
    ],
    checklist: [
      "Daftar sistem dan vendor yang menyimpan data pribadi.",
      "Pemberitahuan privasi dan rumusan persetujuan yang berlaku sekarang.",
      "Kontrak vendor yang memuat unsur pemrosesan data.",
      "Informasi transfer data lintas negara, bila ada.",
    ],
    pitfalls: [
      "Menyalin pemberitahuan GDPR asing tanpa menyandingkannya dengan syarat PDPA.",
      "Persetujuan digabung menjadi satu sehingga sulit dijadikan dasar.",
      "Tidak memiliki daftar kegiatan pemrosesan — hal pertama yang ditanyakan saat peninjauan.",
      "Kontrak vendor tanpa klausul pemrosesan data.",
    ],
    remote: "Sepenuhnya jarak jauh; sesi kerja dilakukan lewat panggilan video.",
  },
  "misc-official": {
    label: "Dokumen resmi lainnya",
    definition:
      "Kelompok ini mencakup dokumen resmi yang tidak berada di bawah satu kementerian tertentu: izin, sertifikat, surat keterangan, permintaan salinan arsip, beserta pengesahan dan terjemahannya untuk dipakai di dalam maupun luar Thailand.",
    chain: [
      "Menentukan instansi penerbit dan set formulir yang berlaku.",
      "Menghimpun daftar bukti dan memeriksa dokumen identitas.",
      "Mengajukan dan memantau melalui nomor referensi.",
      "Terjemahan bersertifikat bila dokumen dibawa ke luar negeri.",
      MFA,
    ],
    checklist: [
      "Paspor atau KTP Thailand pemohon.",
      "Sertifikat, izin, atau nomor referensi yang sudah ada.",
      "Bukti pendukung yang disebut dalam formulir instansi.",
      "Surat kuasa bila pengajuan diwakilkan.",
    ],
    pitfalls: [
      "Mengajukan ke instansi yang keliru sehingga harus mengantre dari awal.",
      "Bukti dalam daftar tidak lengkap.",
      "Menerjemahkan sebelum dokumen asli berbahasa Thai difinalkan.",
      "Mengabaikan masa berlaku dokumen pendukung.",
    ],
    remote: "Pengajuan dan pemantauan kami tangani bila perwakilan diizinkan.",
  },
  council: {
    label: "Pengesahan melalui Lawyers Council of Thailand",
    definition:
      "Sebagian pengesahan hanya boleh dilakukan advokat berlisensi Lawyers Council of Thailand — mencakup pekerjaan Notarial Services Attorney serta pengesahan dokumen dan salinan oleh advokat untuk dipakai di luar negeri.",
    chain: [
      "Konfirmasi apa yang ingin disahkan instansi penerima dan dengan rumusan apa.",
      "Pemeriksaan identitas dan kewenangan penanda tangan.",
      "Advokat berlisensi mengesahkan dan mencatat di buku register.",
      MFA,
      "Penyerahan dokumen asli tersahkan beserta lembar cek.",
    ],
    checklist: [
      "Paspor atau KTP Thailand tiap penanda tangan.",
      "Dokumen versi final yang belum ditandatangani.",
      "Dokumen kewenangan perusahaan bila menandatangani atas nama perusahaan.",
      "Permintaan tertulis instansi penerima, bila ada.",
    ],
    pitfalls: [
      "Dokumen sudah ditandatangani sebelum jadwal pengesahan.",
      "Mengesahkan salinan padahal yang dibutuhkan dokumen asli.",
      "Ejaan nama tidak sesuai paspor.",
      "Melewatkan legalisasi padahal negara tujuan tetap memintanya.",
    ],
    remote: "Peninjauan dan tahap lanjutan jarak jauh; penyaksian tanda tangan perlu kehadiran.",
  },
  "due-diligence": {
    label: "Uji tuntas dan verifikasi (due diligence)",
    definition:
      "Uji tuntas adalah pembuktian atas apa yang dinyatakan mitra: status badan hukum, direktur dan pemegang saham, sertifikat tanah, risiko sengketa, dan keaslian dokumen — bertumpu pada arsip resmi, bukan pada janji lisan.",
    chain: [
      "Menetapkan lingkup: badan hukum, aset, dan pertanyaan risiko yang harus dijawab.",
      "Menarik data arsip (DBD, kantor pertanahan, daftar perkara) sejauh dapat diakses.",
      "Memverifikasi keaslian dokumen dan kewenangan penanda tangan.",
      "Terjemahan bersertifikat atas temuan utama dan dokumen sumber.",
      "Laporan tertulis dengan peringkat masalah dan rekomendasi langkah berikutnya.",
    ],
    checklist: [
      "Nama, nomor registrasi, dan alamat mitra.",
      "Nomor sertifikat tanah atau identitas aset.",
      "Draf kontrak dan pernyataan jaminan yang telah diberikan.",
      "Dokumen yang telah diserahkan pihak mitra.",
    ],
    pitfalls: [
      "Memercayai dokumen dari mitra tanpa menyandingkannya dengan arsip resmi.",
      "Melewatkan pemeriksaan hipotek dan pembatasan di kantor pertanahan.",
      "Menandatangani kontrak sebelum laporan selesai.",
      "Menganggap hasil penelusuran berlaku selamanya — arsip terus berubah.",
    ],
    remote: "Penelusuran dan pelaporan jarak jauh; kunjungan lapangan diatur sesuai permintaan.",
  },
  pension: {
    label: "Dokumen pensiun dan tunjangan luar negeri",
    definition:
      "Lembaga pembayar pensiun dan tunjangan di luar negeri secara berkala meminta bukti masih hidup (life certificate), konfirmasi alamat, dan status perkawinan, umumnya harus disahkan advokat atau notaris dan kadang dilegalisasi.",
    chain: [
      "Konfirmasi formulir dan tingkat pengesahan yang diminta lembaga pembayar.",
      "Pengisian formulir serta pengumpulan bukti identitas dan alamat.",
      "Advokat atau notaris mengesahkan tanda tangan dan salinan.",
      MFA,
      "Pengiriman ke lembaga pembayar dan konfirmasi penerimaan.",
    ],
    checklist: [
      "Paspor serta dokumen tempat tinggal dan keimigrasian di Thailand.",
      "Formulir lembaga pembayar dalam keadaan belum ditandatangani.",
      "Bukti alamat di Thailand.",
      "Akta nikah atau dokumen catatan sipil bila hak Anda bergantung padanya.",
      "Data rekening bank dalam format yang diminta.",
    ],
    pitfalls: [
      "Menandatangani surat keterangan hidup sebelum jadwal pengesahan.",
      "Melewati tenggat tahunan sehingga pembayaran bisa dihentikan sementara.",
      "Tingkat pengesahan lebih rendah dari yang diminta lembaga.",
      "Bukti alamat tidak cocok dengan catatan keimigrasian.",
    ],
    remote: "Semua tahap kecuali penyaksian tanda tangan kami kerjakan untuk Anda.",
  },
  "work-permit": {
    label: "Berkas izin kerja (work permit)",
    definition:
      "Izin kerja Thailand diterbitkan Department of Employment berdasarkan bukti dari pemberi kerja sekaligus pekerja. Kami menyiapkan dan mengurutkan dokumen serta memberi arahan langkah visa yang menyertainya; keputusan tetap ada pada instansi penerbit.",
    chain: [
      "Konfirmasi jenis izin, syarat perusahaan, dan daftar dokumen yang berlaku.",
      "Menyiapkan dokumen perusahaan: affidavit, laporan keuangan, bukti rasio tenaga kerja.",
      "Menyiapkan dokumen pekerja: ijazah, surat referensi, surat keterangan medis, foto.",
      "Terjemahan bersertifikat dan legalisasi ijazah luar negeri bila diperlukan.",
      "Pengajuan, pemantauan, dan penyelarasan masa izin dengan visa atau perpanjangannya.",
    ],
    checklist: [
      "Company affidavit, kutipan DBD, dan pendaftaran VAT.",
      "Paspor pekerja dengan kategori visa yang tepat.",
      "Ijazah dan transkrip beserta terjemahan bersertifikat.",
      "Surat keterangan medis sesuai format yang berlaku.",
      "Kontrak kerja dan uraian jabatan.",
    ],
    pitfalls: [
      "Masuk dengan visa turis padahal kategori izin membutuhkan visa lain.",
      "Ijazah luar negeri belum diterjemahkan bersertifikat dan dilegalisasi.",
      "Perusahaan belum memenuhi rasio tenaga kerja atau modal saat pengajuan.",
      "Tanggal berakhir izin kerja dan visa tidak selaras.",
    ],
    remote:
      "Penyiapan dan pendampingan pengajuan jarak jauh; pemeriksaan medis dan tahap di loket harus langsung.",
  },
  expert: {
    label: "Kasus dokumen kompleks dan penanganan khusus",
    definition:
      "Sebagian berkas tidak mengikuti jalur standar: rantai lintas beberapa negara, permintaan rumusan yang tidak biasa, berkas yang pernah ditolak, atau dokumen yang butuh beberapa lapis pengesahan dalam satu urutan tertentu. Kasus seperti ini ditangani dengan pendekatan konsultatif.",
    chain: [
      "Meninjau perkara: apa sebenarnya yang diminta instansi penerima, secara tertulis.",
      "Merancang rantai pengesahan dan urutan yang wajib dijalankan.",
      "Memperbaiki kesalahan pada dokumen yang ada sebelum pengesahan berikutnya.",
      "Menjalankan rantai: terjemahan, notarisasi, legalisasi Kemlu, kedutaan.",
      "Penyerahan disertai memo pengajuan agar instansi penerima mudah memeriksanya.",
    ],
    checklist: [
      "Seluruh dokumen yang sudah dibuat, termasuk yang pernah ditolak.",
      "Surat alasan penolakan atau surat permintaan, bila ada.",
      "Paspor atau dokumen perusahaan para pihak.",
      "Negara tujuan, instansi penerima, dan tenggat waktunya.",
    ],
    pitfalls: [
      "Mengulang rantai pengesahan yang pernah ditolak tanpa membereskan penyebabnya.",
      "Urutan pengesahan keliru sehingga versi final tidak tercakup.",
      "Mengikuti informasi lisan alih-alih rumusan resmi instansi.",
      "Tidak menyediakan waktu untuk putaran kedua pada kasus kompleks.",
    ],
    remote:
      "Penilaian dan pelaksanaan rantai pengesahan jarak jauh; hanya penyaksian tanda tangan yang perlu kehadiran.",
  },
};
